import React, { useState, useEffect } from 'react';
import { Bell, X, Eye, Info, CheckCircle, AlertTriangle, Package, Settings } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { sanitizeHtml, sanitizeHtmlPreview } from '../utils/sanitizeHtml';

interface Announcement {
  id: number;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'danger' | 'feature' | 'maintenance';
  priority: number;
  created_by_name: string;
  created_at: string;
  is_read?: boolean;
  is_dismissed?: boolean;
}

// Custom hook to get announcement count
export const useAnnouncementCount = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      if (!user) {
        return;
      }

      try {
        const response = await apiService.getUnreadPopupAnnouncements();
        setUnreadCount(response.count || 0);
      } catch (error) {
        console.error('Error fetching announcement count:', error);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000);

    // Listen for refresh events
    const handleRefresh = () => fetchCount();
    window.addEventListener('refreshAnnouncementCount', handleRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshAnnouncementCount', handleRefresh);
    };
  }, [user]);

  return unreadCount;
};

const AnnouncementPopup: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Sadece yetkili kullanıcılar için (veya tüm kullanıcılar için - tercihe göre)
  if (!user) {
    return null;
  }

  const fetchUnreadAnnouncements = async () => {
    try {
      const response = await apiService.getUnreadPopupAnnouncements();
      setAnnouncements(response.announcements || []);
      setUnreadCount(response.count || 0);
      // Dispatch event to refresh count in Header
      window.dispatchEvent(new Event('refreshAnnouncementCount'));
    } catch (error) {
      console.error('Error fetching unread announcements:', error);
    }
  };

  useEffect(() => {
    fetchUnreadAnnouncements();

    // Her 5 dakikada bir yenile
    const interval = setInterval(fetchUnreadAnnouncements, 5 * 60 * 1000);

    // Listen for open event from Header
    const handleOpenAnnouncements = () => {
      setShowModal(true);
    };
    window.addEventListener('openAnnouncements', handleOpenAnnouncements);

    return () => {
      clearInterval(interval);
      window.removeEventListener('openAnnouncements', handleOpenAnnouncements);
    };
  }, []);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info':
        return Info;
      case 'success':
        return CheckCircle;
      case 'warning':
      case 'danger':
        return AlertTriangle;
      case 'feature':
        return Package;
      case 'maintenance':
        return Settings;
      default:
        return Bell;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info':
        return 'border-primary/30 bg-primary/10 text-primary-dark';
      case 'success':
        return 'border-green-200 bg-green-50 text-green-700';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 text-yellow-700';
      case 'danger':
        return 'border-red-200 bg-red-50 text-red-700';
      case 'feature':
        return 'border-purple-200 bg-purple-50 text-purple-700';
      case 'maintenance':
        return 'border-gray-200 bg-gray-50 text-gray-700';
      default:
        return 'border-primary/30 bg-primary/10 text-primary-dark';
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'info':
        return 'text-primary';
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'danger':
        return 'text-red-600';
      case 'feature':
        return 'text-purple-600';
      case 'maintenance':
        return 'text-gray-600';
      default:
        return 'text-primary';
    }
  };

  const handleMarkAsRead = async (announcementId: number) => {
    try {
      await apiService.markAnnouncementAsRead(announcementId);

      // Remove from list
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Refresh count in Header
      window.dispatchEvent(new Event('refreshAnnouncementCount'));
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  };

  const handleDismiss = async (announcementId: number) => {
    try {
      await apiService.markAnnouncementAsDismissed(announcementId);

      // Remove from list
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Refresh count in Header
      window.dispatchEvent(new Event('refreshAnnouncementCount'));
    } catch (error) {
      console.error('Error dismissing announcement:', error);
    }
  };

  const showDetail = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDetailModal(true);
    handleMarkAsRead(announcement.id);
  };

  return (
    <>
      {/* Announcements List Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/20 rounded-full">
                    <Bell className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('announcement.title')}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {t('announcement.count', { count: unreadCount })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Announcements List */}
            <div className="flex-1 overflow-y-auto p-6">
              {announcements.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Bell className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>{t('announcement.empty')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {announcements.map((announcement) => {
                    const IconComponent = getTypeIcon(announcement.type);
                    const typeColor = getTypeColor(announcement.type);
                    const iconColor = getIconColor(announcement.type);

                    return (
                      <div
                        key={announcement.id}
                        className={`border-l-4 border rounded-lg p-4 shadow-sm ${typeColor} relative`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={`p-2 rounded-full bg-white ${iconColor}`}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="text-base font-semibold text-gray-900 truncate">
                                  {announcement.title}
                                </h4>
                                {announcement.priority > 3 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {t('announcement.high_priority')}
                                  </span>
                                )}
                              </div>
                              <div
                                className="text-sm text-gray-700 mb-3 line-clamp-2"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtmlPreview(announcement.content, 150)
                                }}
                              />
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-500">
                                  {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString('tr-TR')}
                                </p>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => showDetail(announcement)}
                                    className="px-3 py-1.5 text-sm bg-white text-primary border border-primary/30 rounded-md hover:bg-primary/10 transition-colors"
                                  >
                                    <Eye className="h-4 w-4 inline mr-1" />
                                    {t('announcement.view')}
                                  </button>
                                  <button
                                    onClick={() => handleDismiss(announcement.id)}
                                    className="px-3 py-1.5 text-sm bg-white text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                                  >
                                    <X className="h-4 w-4 inline mr-1" />
                                    {t('announcement.close')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                {t('announcement.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-full ${getIconColor(selectedAnnouncement.type)} bg-gray-100`}>
                    {React.createElement(getTypeIcon(selectedAnnouncement.type), { className: "h-6 w-6" })}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {selectedAnnouncement.title}
                    </h2>
                    <div className="flex items-center space-x-3 mt-1">
                      <p className="text-sm text-gray-500">
                        {selectedAnnouncement.created_by_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedAnnouncement.created_at).toLocaleString('tr-TR')}
                      </p>
                      {selectedAnnouncement.priority > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {t('announcement.high_priority')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedAnnouncement.content) }}
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('announcement.close')}
              </button>
              <button
                onClick={() => {
                  handleDismiss(selectedAnnouncement.id);
                  setShowDetailModal(false);
                  setShowModal(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {t('announcement.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnnouncementPopup;
